import { css } from '@emotion/css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Converter } from 'showdown';
import { setOpenedCloseSurveyModal, setOpenedQuestionnaire } from 'store/questionnaireSlice';
import { RootState } from 'store/store';
import { colors } from 'styles/colors';
import * as SurveyCore from 'survey-core';
import { Model } from 'survey-core';
import 'survey-core/defaultV2.min.css';
import { Survey } from 'survey-react-ui';
import * as widgets from 'surveyjs-widgets';
import { nouislider } from 'surveyjs-widgets';
import { QuestionnaireAPI } from 'utils/QuestionnaireAPI';

import CloseSurveyModal from 'modals/CloseSurveyModal';
import ErrorSurveyModal from 'modals/ErrorSurveyModal';
import { useNavigate } from 'react-router-dom';
import { light_theme_survey } from 'utils/theme_light_survey';

import { useMediaQuery } from '@mui/material';
import Return from 'assets/icons/back-arrow.svg';
import FullscreenExit from 'assets/icons/fullscreen-exit.svg';
import Fullscreen from 'assets/icons/fullscreen.svg';

// Extend the Window interface to include ReactNativeWebView
declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage: (message: string) => void;
        };
    }
}

nouislider(SurveyCore);

widgets.ckeditor(SurveyCore);

type QuestionnairePageProps = {
    questionnaireId?: string;
    json?: string;
    version?: string;
    questionnaireName?: string;
    dataMentorQuestionnaireId: string | null
};

const QuestionnairePage = ({ json, questionnaireId, version, questionnaireName, dataMentorQuestionnaireId }: QuestionnairePageProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const matches = useMediaQuery('(min-width:580px)');

    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [surveyJson, setSurveyJson] = useState<string | undefined>(json);
    const [surveyVersion, setSurveyVersion] = useState<string | undefined>(version);
    const token = useSelector((state: RootState) => state.global.authToken);
    const participantDetails = useSelector((state: RootState) => state.user.participantDetails);

    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<boolean>(false);
    const isOnMobile = 'ReactNativeWebView' in window && window.ReactNativeWebView;

    const surveyComplete = useCallback(
        async (model: SurveyCore.Model) => {
            const surveyResponse = JSON.stringify(model.data);
            if (isOnMobile) {
                // Send survey response data to mobile WebView receiver
                window.ReactNativeWebView?.postMessage(surveyResponse);
            } else {
                const participantId = participantDetails?.participantId;
                // Send survey response data to CBMR and mark survey as complete
                const startedTime = localStorage.getItem('startQuestionnaireDate');
                const currentDate = Date.now();
                const startDateNumeric = startedTime ? parseInt(startedTime, 10) : 0;
                const secondsToComplete = Math.round((currentDate - startDateNumeric) / 1000);
                if (participantId && questionnaireId && token && version && questionnaireName && startedTime) {
                    const formattedStartedTime = new Date(Number(startedTime)).toISOString().split('.')[0];
                    try {
                        await QuestionnaireAPI.sendQuestionnaireCompleted(
                            participantId,
                            questionnaireId,
                            questionnaireName,
                            version,
                            formattedStartedTime,
                            new Date().toISOString(),
                            model.data,
                            token,
                            dataMentorQuestionnaireId
                        );
                        await QuestionnaireAPI.setQuestionnaireCompleted(
                            questionnaireId,
                            secondsToComplete,
                            version,
                            token
                        );
                        setIsCompleted(true);
                        navigate('/questionnaries-list');
                    } catch {
                        setIsCompleted(true);
                        setUploadError(true);
                    }
                }
            }
        },
        [dataMentorQuestionnaireId, isOnMobile, navigate, participantDetails?.participantId, questionnaireId, questionnaireName, token, version]
    );

    const surveyClose = useCallback(async () => {
        if (isOnMobile) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ action: 'returnArrowClicked' }));
        } else {
            isCompleted ? navigate('/questionnaries-list') : dispatch(setOpenedCloseSurveyModal(true));
        }
    }, [dispatch, isCompleted, isOnMobile, navigate]);

    const turnFullScreen = useCallback(async () => {
        setIsFullscreen(!isFullscreen);
    }, [isFullscreen]);

    useEffect(() => {
        (window as any).setSurveyJson = setSurveyJson;
        (window as any).setSurveyVersion = setSurveyVersion;
    }, [json]);

    useEffect(() => {
        const currentDate = Date.now();
        localStorage.setItem('startQuestionnaireDate', currentDate.toString());

        return () => {
            dispatch(setOpenedQuestionnaire(null));
        };
    }, [dispatch]);

    const survey = useMemo(() => {
        const s = new Model(surveyJson);
        const theme: any = light_theme_survey;
        s.applyTheme(theme);
        s.focusFirstQuestionAutomatic = false;
        s.locale = 'da';
        return s;
    }, [surveyJson]);

    useEffect(() => {
        survey.onComplete.add(surveyComplete);
        return () => {
            survey.onComplete.remove(surveyComplete);
        };
    }, [survey, surveyComplete]);

    useMemo(() => {
        const converter = new Converter();
        survey.onTextMarkdown.add(function (_survey: any, options) {
            let str = converter.makeHtml(options.text);
            // eslint-disable-next-line eqeqeq
            if (str && str.indexOf('<p>') == 0) {
                str = str.substring(3);
                str = str.substring(0, str.length - 4);
            }
            options.html = str;
        });
    }, [survey.onTextMarkdown]);

    return (
        <div className={styles.container}>
            <div className={styles.returnArrow}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    style={isOnMobile ? { width: '2rem', height: '1.5rem' } : { width: '2rem', height: '2rem' }}
                >
                    <image
                        href={Return}
                        style={
                            isOnMobile
                                ? { width: '2rem', height: '1.5rem' }
                                : { width: '2rem', height: '2rem', cursor: 'pointer' }
                        }
                        onClick={() => surveyClose()}
                    />
                </svg>
            </div>
            {!isFullscreen && ('ReactNativeWebView' in window && window.ReactNativeWebView) === false && (
                <div className={styles.fullscreen}>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '2rem', height: '2rem' }}>
                        <image
                            href={Fullscreen}
                            style={{ width: '2rem', height: '2rem', cursor: 'pointer' }}
                            onClick={() => turnFullScreen()}
                        />
                    </svg>
                </div>
            )}

            {isFullscreen && ('ReactNativeWebView' in window && window.ReactNativeWebView) === false && (
                <div className={styles.fullscreen}>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '2rem', height: '2rem' }}>
                        <image
                            href={FullscreenExit}
                            style={{ width: '2rem', height: '2rem', cursor: 'pointer' }}
                            onClick={() => turnFullScreen()}
                        />
                    </svg>
                </div>
            )}
            <div
                className={styles.content}
                style={{
                    width:
                        ('ReactNativeWebView' in window && window.ReactNativeWebView) || isFullscreen ? '95vw' : matches ? '80vw' : '100%',
                }}
            >
                <Survey model={survey} />
            </div>
            <div className={styles.footer}>
                <span className={styles.footerText}>Version {surveyVersion || version}</span>
            </div>
            <CloseSurveyModal />
            <ErrorSurveyModal
                open={uploadError}
                text={'Din besvarelse kunne ikke uploades. Du kan prøve igen ved at besvare spørgeskemaet på ny. Kontakt venligst support, hvis problemet fortsætter.'}
                onClose={() => {
                    setUploadError(false);
                    navigate('/questionnaries-list');
                }}
            />
        </div>
    );
};

const styles = {
    container: css({
        background: colors.backgroundLight,
        marginTop: 'ReactNativeWebView' in window && window.ReactNativeWebView ? '0' : '4.5rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'ReactNativeWebView' in window && window.ReactNativeWebView ? 'calc(100vh)' : '92vh',
        width: '100%',
        fontFamily: 'Figtree !important',
        '.sd-body__progress--bottom .sd-progress__text': {
            paddingTop: '6px',
        },
        '.sd-page': {
            fontFamily: 'Figtree !important',
        },
        '.sd-header__text .sd-title': {
            fontFamily: 'Figtree !important',
            fontWeight: 'normal',
        },
        '.sd-root-modern .sd-container-modern__title .sd-description': {
            fontFamily: 'Figtree !important',
        },
        '.sd-page .sd-page__description': {
            fontFamily: 'Figtree !important',
            color: colors.primaryText,
        },
        '.sd-element__title .sd-element__num': {
            fontFamily: 'Figtree !important',
            color: colors.primaryText,
            fontSize: '16px',
        },
        '.sd-root-modern': {
            fontFamily: 'Figtree !important',
        },
        '.sd-title.sd-element__title': {
            fontFamily: 'Figtree !important',
        },
        '.sd-item__control-label': {
            fontFamily: 'Figtree !important',
        },
        '.sd-completedpage h3, .sd-completed-before-page h3': {
            fontFamily: 'Figtree !important',
        },
        '.sd-root-modern .sd-container-modern__title': {
            background: colors.backgroundLight,
        },
        '.sd-root-modern__wrapper': {
            background: colors.backgroundLight,
        },
        '.sd-body__navigation .sd-btn': {
            background: colors.primary,
            color: colors.primaryTextLight,
        },
        '.noUi-connect, .progress-bar': {
            backgroundColor: 'rgb(29, 41, 81)',
        },
    }),
    content: css({
        flex: 1,
        margin: '0 auto',
        display: 'flex',
        marginTop: '1.5rem',
    }),
    footer: css({
        height: '1.5rem',
    }),
    footerText: css({
        color: '#605E5C',
        fontFamily: 'Segoe UI',
        fontSize: '0.8125rem',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '1.25rem',
        marginLeft: '1rem',
    }),
    returnArrow: css({
        position: 'absolute',
        width: '2rem',
        flexShrink: '0',
        marginLeft: '1rem',
        marginTop: '1rem',
        zIndex: 9000,
        cursor: 'auto',

        outline: 'none',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
        ':active': {
            backgroundColor: 'transparent',
        },
        '-webkit-tap-highlight-color': 'transparent',
    }),
    fullscreen: css({
        position: 'absolute',
        width: '2rem',
        flexShrink: '0',
        right: '1rem',
        marginTop: '1rem',
        zIndex: 9000,
        cursor: 'auto',

        outline: 'none',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
        ':active': {
            backgroundColor: 'transparent',
        },
        '-webkit-tap-highlight-color': 'transparent',
    }),
};

export default QuestionnairePage;
